import { parseMaybeAvatarImage } from '@/apis/repositories/parsers/match'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import { convertUserGenderToSex } from '@/libs/utilities/formatUserProfile'
import type { Match, RoomID } from '@/types/app'
import type { Ref } from 'vue'
import { computed } from 'vue'
import {
  useColleagueChatRoomOnUseMatchQuery,
  useOwnChatRoomOnUseMatchQuery,
} from './useMatch.generated'

const useChatRoom = (
  roomId: Ref<Maybe<RoomID>>,
  owner: 'own' | 'colleague'
) => {
  const ownChatRoomQuery = useOwnChatRoomOnUseMatchQuery(
    computed(() => ({
      id: roomId.value?.toString() ?? '',
    })),
    () => ({
      enabled: owner === 'own' && roomId.value != null,
    })
  )
  const colleagueChatRoomQuery = useColleagueChatRoomOnUseMatchQuery(
    computed(() => ({
      id: roomId.value?.toString() ?? '',
    })),
    () => ({
      enabled: owner === 'colleague' && roomId.value != null,
    })
  )

  const refetch = () => {
    switch (owner) {
      case 'own':
        return ownChatRoomQuery.refetch()
      case 'colleague':
        return colleagueChatRoomQuery.refetch()
      default:
        return assertExhaustive(owner)
    }
  }

  //
  // FIXME: 以下ひとつの computed, switch で記述するともっとスマートだと思う
  //
  const chatRoom = computed(() => {
    switch (owner) {
      case 'own':
        return ownChatRoomQuery.result.value?.ownChatRoom
      case 'colleague':
        return colleagueChatRoomQuery.result.value?.colleagueChatRoom
      default:
        return assertExhaustive(owner)
    }
  })

  const error = computed(() => {
    switch (owner) {
      case 'own':
        return ownChatRoomQuery.error.value
      case 'colleague':
        return colleagueChatRoomQuery.error.value
      default:
        return assertExhaustive(owner)
    }
  })

  const loading = computed(() => {
    switch (owner) {
      case 'own':
        return ownChatRoomQuery.loading.value
      case 'colleague':
        return colleagueChatRoomQuery.loading.value
      default:
        return assertExhaustive(owner)
    }
  })

  return {
    chatRoom,
    refetch,
    error,
    loading,
  }
}

export const useMatch = (
  roomId: Ref<Maybe<RoomID>>,
  owner: 'own' | 'colleague'
) => {
  const { chatRoom, refetch, loading, error } = useChatRoom(roomId, owner)

  const match = computed<Match | undefined>(() => {
    if (!chatRoom.value?.planner.chatParticipant || !chatRoom.value?.user) {
      return undefined
    }

    const m: Match = {
      id: Number(chatRoom.value.match.id),
      roomId: Number(chatRoom.value.id),
      chatRoomStatus: chatRoom.value.status,
      hasUnrepliedMessage: chatRoom.value.hasUnrepliedMessage,
      user: {
        id: Number(chatRoom.value.user.id),
        yearOfBirth: chatRoom.value.user.birthYear,
        nickname: chatRoom.value.user.nickname,
        sex: convertUserGenderToSex(chatRoom.value.user.gender),
        livingPrefecture: chatRoom.value.user.livingPrefecture,
        address: chatRoom.value.user.address,
        diagnosisResult: chatRoom.value.user.diagnosisResult,
        financialServicesAgent: chatRoom.value.user.financialServicesAgent,
        userRegistrationInflowSource:
          chatRoom.value.user.userRegistrationInflowSource,
      },
      planner: {
        id: Number(chatRoom.value.planner.id),
        name: chatRoom.value.planner.name,
        avatarImage: parseMaybeAvatarImage(
          Number(chatRoom.value.planner.id),
          chatRoom.value.planner.avatar ?? undefined
        ),
        chatParticipantId: Number(chatRoom.value.planner.chatParticipant.id),
      },
      latestMessage: undefined, // NOTE: とりあえず見なくて良い,
      userLatestReadMessageGUID: chatRoom.value.userLatestReadMessageGUID
        ? chatRoom.value.userLatestReadMessageGUID
        : undefined,
      normalMessageChatReplyMatchSetting: {
        disabledUntil: chatRoom.value.match.normalMessageChatReplyMatchSetting
          .disabledUntil
          ? new Date(
              chatRoom.value.match.normalMessageChatReplyMatchSetting.disabledUntil
            )
          : undefined,
      },
    }

    return m
  })

  return {
    match,
    refetch,
    error,
    loading,
  }
}
